import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/pages/home/components/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [filteredMovies, setFilteredMovies] = useState(movies);

  useEffect(() => {
    setActiveSlug(slug || 'all');
  }, [slug]);

  useEffect(() => {
    if (activeSlug === 'all') {
      setFilteredMovies(movies);
    } else {
      setFilteredMovies(movies.filter((m) => m.categorySlug === activeSlug));
    }
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  useEffect(() => {
    const ldJson = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://www.yemaocinema.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: currentCategory?.name || '全部',
              item: `https://www.yemaocinema.com/category/${activeSlug}`,
            },
          ],
        },
        {
          '@type': 'WebPage',
          name: `夜猫影院 - ${currentCategory?.name || '全部'}影视`,
          url: `https://www.yemaocinema.com/category/${activeSlug}`,
          isPartOf: { '@id': 'https://www.yemaocinema.com/#website' },
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(ldJson);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [activeSlug, currentCategory]);

  return (
    <>
      <Navbar />
      <main className="pt-16 min-h-screen">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4 border-b border-background-700/30">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
              <span className="text-foreground-700">/</span>
              <span className="text-foreground-300">{currentCategory?.name || '全部'}影视</span>
            </nav>
          </div>
        </div>

        {/* Header */}
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-2">
              {currentCategory?.name || '全部'}影视
            </h1>
            <p className="text-foreground-500 text-sm">
              {activeSlug === 'all'
                ? '浏览全部精彩影视内容，找到你的深夜观影之选'
                : `精选${currentCategory?.name}类影视，深夜追剧不孤单`}
            </p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="w-full px-4 md:px-6 pb-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-950'
                      : 'bg-background-800/50 text-foreground-400 hover:text-foreground-200 hover:bg-background-700/50 border border-background-700/30'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Movie Grid */}
        <div className="w-full px-4 md:px-6 pb-14">
          <div className="max-w-7xl mx-auto">
            {filteredMovies.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                {/* Pagination hint */}
                <div className="mt-8 text-center">
                  <p className="text-foreground-600 text-sm">
                    共 {filteredMovies.length} 部{currentCategory?.name || ''}影视作品
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-800/50 mb-4">
                  <i className="ri-movie-line text-foreground-600 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无该分类的影视内容</p>
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-1 mt-4 text-primary-400 hover:text-primary-300 text-sm cursor-pointer whitespace-nowrap"
                >
                  返回全部影视
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}